import { useMemo } from "react";

import type { NotificationRow as InboxStandardFragment } from "#app/lib/data";
import { formatDateKey } from "@salestrak/ui";
import { useDateBoundaryKey } from "#app/lib/hooks/use-date-boundary-key";

export interface InboxDateGroup {
  /** Bucket key — YYYY-MM-DD for Today/Yesterday, YYYY-MM for month buckets.
   *  Matches the group's `date-group-${dateKey}` DOM id. */
  dateKey: string;
  /** Human label ("Today", "Yesterday", "June", "June 2025"). */
  dateLabel: string;
  items: InboxStandardFragment[];
}

export interface InboxDateGroups {
  groups: InboxDateGroup[];
  /** All days with items, sorted descending — feeds the jump-to-date popover.
   *  Derived from loaded items, so it grows as the feed paginates. */
  dateKeys: string[];
}

/**
 * Buckets the feed the way the notification-inbox reference does: a "Today"
 * and a "Yesterday" bucket for the freshest items, then one bucket per
 * calendar month ("June"; months outside the current year carry it — "June
 * 2025"). Every bucket renders as one bordered bundle of flush rows, so the
 * per-row date column carries the day and the header carries only the coarse
 * bucket.
 */
export function useInboxDateGroups(
  items: InboxStandardFragment[],
  ordering: "newest" | "oldest",
): InboxDateGroups {
  // Re-bucket when the viewer's local day rolls over, so "Today" doesn't
  // silently keep yesterday's items behind a stale header (the B8 bug class).
  const todayKey = useDateBoundaryKey();

  return useMemo(() => {
    const today = new Date(`${todayKey}T00:00:00`);
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayKey = formatDateKey(yesterday);
    const currentYear = today.getFullYear();

    const bucketOf = (date: Date, dayKey: string) => {
      if (dayKey === todayKey) return { key: dayKey, label: "Today" };
      if (dayKey === yesterdayKey) return { key: dayKey, label: "Yesterday" };
      // Month bucket. The key (YYYY-MM) is a prefix of its member day keys,
      // so descending string order interleaves correctly with the full-day
      // Today/Yesterday keys ("2026-08-18" > "2026-08-17" > "2026-08").
      return {
        key: dayKey.slice(0, 7),
        label: date.toLocaleDateString(
          undefined,
          date.getFullYear() === currentYear
            ? { month: "long" }
            : { month: "long", year: "numeric" },
        ),
      };
    };

    const map = new Map<string, InboxDateGroup>();
    const daySet = new Set<string>();

    for (const item of items) {
      const date = new Date(item.creationDate);
      const dayKey = formatDateKey(date);
      daySet.add(dayKey);
      const bucket = bucketOf(date, dayKey);
      const group = map.get(bucket.key);
      if (group) {
        group.items.push(item);
      } else {
        map.set(bucket.key, {
          dateKey: bucket.key,
          dateLabel: bucket.label,
          items: [item],
        });
      }
    }

    // dateKeys is always sorted descending regardless of display ordering, so
    // the popover's "earliest in range" math is correct either way.
    const dateKeys = [...daySet].sort((a, b) => (a < b ? 1 : a > b ? -1 : 0));

    // Sort buckets AND the items inside each: items arrive ordered by
    // `updated_at DESC`, not `creationDate`, and a month bucket spans many
    // days — unsorted rows would zig-zag through the month.
    const groups = [...map.values()].sort((a, b) =>
      a.dateKey < b.dateKey ? 1 : a.dateKey > b.dateKey ? -1 : 0,
    );
    for (const group of groups) {
      group.items.sort(
        (a, b) =>
          new Date(b.creationDate).getTime() -
          new Date(a.creationDate).getTime(),
      );
    }
    if (ordering === "oldest") {
      groups.reverse();
      for (const group of groups) group.items.reverse();
    }

    return { groups, dateKeys };
  }, [items, ordering, todayKey]);
}
